/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import classNames from "classnames";
import { ResponsiveHeroImage, ReviewRating, LazyLoadImages } from "@quintype/components";
import get from "lodash/get";
import { CustomLink } from "../custom-link";
import PropTypes from "prop-types";
import { Author } from "./author.js";
import { ImageFallback } from "./image-fallback.js";
import { altTagOfHeroImages, isExternalStory, getStoryUrl } from "../../utils";
import { CollectionName } from "./collection-name";

export function MediaObject({ story = {}, config = {} }) {
  const author = {
    name: get(story, ["authors", 0, "name"], story["author-name"]),
    id: get(story, ["authors", 0, "id"], story["author-id"])
  };
  const date =
    config.storyTime && (story["last-published-at"] ? story["last-published-at"] : story["first-published-at"]);
  const isReviewStory = story["story-template"] === "review";
  return (
    story && (
      <CustomLink
        href={getStoryUrl(story)}
        externalLink={isExternalStory(story)}
        className={classNames("media", {
          "media--bg-white": !config.noBackground,
          "media--with-shadow": !config.noBackground
        })}
      >
        <div className="media__img">
          {story["hero-image-s3-key"] ? (
            <figure className={`media__img--${story["story-template"]}`}>
              <LazyLoadImages>
                <ResponsiveHeroImage
                  story={story}
                  aspectRatio={config.aspectRatio ? config.aspectRatio : [4, 3]}
                  alt={altTagOfHeroImages(story)}
                  defaultWidth={480}
                  widths={config.widths || [250, 480, 640]}
                  imgParams={{ auto: ["format", "compress"], fit: "max" }}
                  sizes={config.sizes}
                />
              </LazyLoadImages>
            </figure>
          ) : (
            <ImageFallback story={story} />
          )}
        </div>
        <div className={classNames("media__content", { "media__content--enlarged": config.enlarged })}>
          {config.section && (
            <CollectionName
              name={get(story, ["sections", 0, "display-name"]) || get(story, ["sections", 0, "name"])}
              section={get(story[("sections", 0)])}
            />
          )}
          <h2
            className={classNames("media-title", { "media__title--enlarged": config.enlarged })}
            data-test-id="media-object-headline"
          >
            <bdi dangerouslySetInnerHTML={{ __html: story.headline }} />
          </h2>
          {isReviewStory && <ReviewRating value={get(story, ["metadata", "review-rating", "value"], "0")} />}
          <Author author={author} date={date} config={config} />
        </div>
      </CustomLink>
    )
  );
}

export function MediaObjectsList(props) {
  return (
    <div>
      {props.stories.slice(0, 3).map(storyObj => {
        const story = storyObj && storyObj.story ? storyObj.story : storyObj;
        return <MediaObject story={story} key={story.id} />;
      })}
    </div>
  );
}
MediaObjectsList.propTypes = {
  stories: PropTypes.array
};
